/* eslint-disable camelcase */
import type { TwoSymbolLocale } from './locale';

export type VIType =
  | 'consumable'
  | 'non_consumable'
  | 'non_renewing_subscription';

export interface IStoreItemPrice {
  amount: string;
  amount_without_discount: string;
  currency: string;
}

export interface IStoreVirtualPrice extends IStoreItemPrice {
  sku: string;
  is_default: boolean;
  image_url: string;
  name: string;
  type: string;
  description: string;
}

export interface IStoreDRM {
  drm_name: string;
  drm_sku: string;
  has_keys: boolean;
  is_free: boolean;
  is_pre_order: boolean;
  price: IStoreItemPrice;
  sku: string;
  type: string;
  virtual_prices: IStoreVirtualPrice[];
}

export interface StoreUnitGameItem {
  sku: string;
  name: string;
  type: 'unit';
  description: string | void;
  image_url: string;
  attributes: any[];
  groups: any[];
  unit_items: IStoreDRM[];
  unit_type: 'game';
}

export type LocalItemType = 'vi' | 'vc' | 'custom_amount' | 'bundle' | 'gk';

export type BundleType = 'standard' | 'virtual_currency_package';

export enum StoreUniversalItem {
  VIRTUAL_CURRENCY = 'virtual_currency',
  UNIT = 'unit',
  BUNDLE = 'bundle',
  VIRTUAL_GOOD = 'virtual_good',
  VIRTUAL_ITEM = 'virtual_item',
  DIGITAL_CONTENT = 'digital_content',
  GAME_KEY = 'game_key',
}

export type Order = {
  items: Array<{
    sku: string;
    quantity: number;
  }>;
};

export type StoreItemsGroup = {
  external_id: string;
  name: string;
  description: string;
  image_url: string | null;
  level: number;
  order: number;
  children: any[];
  parent_external_id: string | null;
};

export interface StoreItem {
  sku: string;
  name: string;
  groups: StoreItemsGroup[];
  attributes: any[];
  type: StoreUniversalItem;
  description: string;
  image_url: string | null;
  is_free: boolean;
  price: IStoreItemPrice;
  virtual_item_type: VIType;
  virtual_prices: IStoreVirtualPrice[];
  item_id?: number | null;
  unit_items: IStoreDRM[];
  unit_type?: string;
  is_bonus: boolean;
}

export interface StoreBundle extends StoreItem {
  bundle_type: BundleType;
  content: Array<{
    description: string | null;
    image_url: string | null;
    name: string;
    quantity: number;
    sku: string;
    type: string;
    drm_name?: string;
    drm_sku?: string;
  }>;
  total_content_price: IStoreItemPrice;
  inventory_options: {
    consumable: null;
    expiration_period: null;
  };
}

export interface StoreVirtualItem {
  sku: string;
  name: string;
  type: 'virtual_good';
  description: string;
  image_url: string | null;
  attributes: any[];
  is_free: boolean;
  groups: StoreItemsGroup[];
  price: IStoreItemPrice;
  virtual_prices: IStoreVirtualPrice[];
  virtual_item_type: VIType;
  inventory_options: {
    consumable: null;
    expiration_period: null;
  };
}

export interface StoreVirtualCurrency {
  sku: string;
  name: string;
  type: 'virtual_currency';
  description: string;
  image_url: string;
  attributes: any[];
  is_free: boolean;
  groups: any[];
  price: IStoreItemPrice;
  virtual_prices: IStoreVirtualPrice[];
}

export type AnyStoreItem =
  | StoreItem
  | StoreBundle
  | StoreVirtualItem
  | StoreVirtualCurrency;

export interface WarningCartItem {
  errorCode: number;
  errorMessage: string;
  quantity: number;
  sku: string;
}

export type CartWarnings = WarningCartItem[];

export interface CartPrice {
  amount: string;
  amount_without_discount: string;
  currency: string;
}

export interface CartResponse {
  is_free: boolean;
  price: CartPrice;
  items: StoreItem[];
  warnings: CartWarnings;
}

export interface PSSettings {
  ui?: {
    theme: string;
  };
  language?: TwoSymbolLocale;
  disable_saved_methods?: boolean;
}

export interface CreateOrderBody {
  currency?: string;
  locale?: TwoSymbolLocale;
  sandbox?: boolean;
  settings?: PSSettings;
  custom_parameters?: any;
}

export interface CreateOrderResponse {
  token: string;
  order_id: number;
}

export interface StoreItemIdentifier {
  sku: string;
  type: StoreUniversalItem;
  bundleType?: BundleType;
}

export interface CartSuccess {
  price: CartPrice;
  items: any[];
}
